<script setup>
  import AsSvg from '@/components/posters/as-svg'
  import AsPosterSymbol from '@/components/posters/as-poster-symbol'
  import AsFigure from '@/components/profile/as-figure'
  import AsThought from '@/components/thoughts/as-thought'
  import AsAuthorMenu from '@/components/posters/as-menu-author'
  import { defineAsyncComponent as define_async_component } from 'vue'
  import { useMediaQuery as use_media_query } from '@vueuse/core'

  const AsViewer3d = define_async_component(
    () => import('@/components/posters/as-viewer-3d.vue')
  )
  const AsDownload = define_async_component(
    () => import('@/components/download-vector.vue')
  )
  /** @typedef {import('@/types').Id} Id */
  /** @typedef {import('@/types').Poster} Poster */
  /** @typedef {import('@/types').Statement} Statement */
  import {
    as_query_id,
    as_author,
    as_created_at,
    load_from_cache,
    load
  } from '@/utils/itemid'
  import { as_time, as_day } from '@/utils/date'
  import { get } from 'idb-keyval'
  import {
    is_vector,
    is_vector_id,
    is_click,
    has_drawable_layer
  } from '@/use/poster'
  import {
    mosaic,
    view_3d,
    animate as animate_pref,
    enable_geology_layers
  } from '@/utils/preference'
  import { use_mask_pen, subject_hue } from '@/use/mask-pen'
  import { load_cutout_flags, GEOLOGY_DATE } from '@/utils/geology'
  import { load_shadow_into_vector } from '@/utils/poster-layers'
  import { poster_delete_log } from '@/utils/poster-delete-log'
  import { use_poster_viewport_visibility } from '@/use/poster-viewport-visibility'
  import {
    poster_dom_id,
    poster_dom_href,
    POSTER_MEET_TOGGLE_ONLY
  } from '@/use/poster-dom-reference'
  import { use_poster_instance } from '@/use/poster-instances'
  import { is_audio_file, decode_audio_files } from '@/utils/audio-file'
  import { export_poster_to_video_with_audio } from '@/utils/export-poster-video'
  import { use_poster_svg_activate_pointer } from '@/use/poster-svg-activate-pointer'
  import { use_delegated_pan } from '@/use/delegated-pan'
  import { is_ios } from '@/utils/platform'
  import {
    ref,
    computed,
    getCurrentInstance as current_instance,
    watchEffect as watch_effect,
    watch,
    onUpdated as updated,
    onMounted as mounted,
    onBeforeUnmount as before_unmount,
    nextTick as tick,
    provide,
    inject
  } from 'vue'
  const props = defineProps({
    itemid: {
      type: String,
      required: true,
      validate: is_vector_id
    },
    menu: {
      type: Boolean,
      default: false
    },
    slice: {
      type: Boolean,
      default: undefined
    },
    profile_display: {
      type: String,
      default: 'label',
      validator: v => ['label', 'phonebook'].includes(v)
    },
    overlay_statements: {
      /** @type {import('vue').PropType<Statement[] | null>} */
      type: Array,
      default: null
    },
    overlay_editable: {
      type: Boolean,
      default: false
    },
    menu_always_visible: {
      type: Boolean,
      default: false
    },
    pin: {
      type: Boolean,
      default: false
    }
  })
  const emit = defineEmits({
    'vector-click': is_click,
    show: is_vector,
    remove: is_vector_id,
    missing: is_vector_id
  })
  const instance = current_instance()
  const mask_pen = use_mask_pen()
  provide('mask-pen', mask_pen)
  const poster = ref(null)
  provide('pan_delegator', use_delegated_pan(poster))
  const vector = ref(null)
  const person = ref(null)
  const menu_open = ref(false)
  const thought_overlay_open = ref(false)
  const SVG_ZOOM_SCALE = 1.59
  const canvas_alive = ref(false)
  const canvas_leaving = ref(false)
  const layer_comparison =
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover)').matches
  const layer_view = ref('both')
  const reduced_motion = use_media_query('(prefers-reduced-motion: reduce)')
  const focus_pull = ref(true)
  const focus_pull_active = computed(
    () =>
      layer_comparison &&
      canvas_alive.value &&
      layer_view.value === 'both' &&
      focus_pull.value &&
      animate_pref.value &&
      !reduced_motion.value
  )
  const viewer_ref = ref(null)

  const overlay_text_visible = computed(() => {
    if (!props.overlay_statements?.length) return false
    if (mask_pen.active.value) return false
    return thought_overlay_open.value
  })

  const figcaption_visible = computed(
    () =>
      menu_open.value ||
      thought_overlay_open.value ||
      (props.menu && props.menu_always_visible) ||
      overlay_text_visible.value ||
      mask_pen.active.value
  )

  watch(
    () => props.overlay_statements?.map(statement => statement.id).join('\0'),
    () => {
      thought_overlay_open.value = false
    }
  )

  const vector_click = () => {
    if (!props.menu) return
    if (props.menu_always_visible) return
    menu_open.value = !menu_open.value
  }

  const on_toggle_mask_pen = () => {
    const will_activate = !mask_pen.active.value
    if (will_activate && !mosaic.value) {
      mosaic.value = true
      enable_geology_layers()
    }
    mask_pen.toggle_active()
    if (will_activate) menu_open.value = false
  }

  const on_window_pointerdown = e => {
    if (!mask_pen.active.value) return
    if (poster.value && !poster.value.contains(e.target))
      mask_pen.toggle_active()
  }

  const on_poster_svg_click = () => {
    if (mask_pen.active.value) return
    if (props.overlay_statements?.length) {
      thought_overlay_open.value = !thought_overlay_open.value
      if (!props.menu) menu_open.value = thought_overlay_open.value
    }
    vector_click()
  }
  /** True after `load_cutouts` wrote `vector.cutouts`; false after unload or new vector in `on_show`. */
  const cutouts_loaded = ref(false)
  const cutout_load_token = ref(0)
  /** Reads refs outside `load_cutouts` so `require-atomic-updates` does not pair with the post-await write. */
  const should_skip_cutout_load = () =>
    Boolean(cutouts_loaded.value && vector.value?.cutouts)

  provide('mask-pen-symbols-ready', cutouts_loaded)

  const query_id = computed(() => as_query_id(/** @type {Id} */ (props.itemid)))

  const {
    intersecting: poster_intersecting,
    in_view: poster_in_view,
    sync: sync_poster_visibility
  } = use_poster_viewport_visibility(poster)

  const { am_canonical, use_reference, is_referenced } = use_poster_instance(
    () => /** @type {Id} */ (props.itemid),
    { el: poster, intersecting: poster_intersecting, kind: 'poster' }
  )
  const use_dom_reference = use_reference

  mounted(() => {
    if (typeof window !== 'undefined')
      window.addEventListener('pointerdown', on_window_pointerdown)
    tick().then(() => {
      sync_poster_visibility()
      scroll_to_hash_if_matching()
    })
  })

  const scroll_to_hash_if_matching = () => {
    if (typeof window === 'undefined') return
    const fragment = window.location.hash.substring(1)
    if (query_id.value === fragment && poster.value) {
      poster.value.scrollIntoView({ behavior: 'smooth', block: 'center' })
      window.location.hash = ''
    }
  }

  const sync_poster_for_svg = computed(() =>
    is_referenced.value && vector.value && is_vector(vector.value)
      ? vector.value
      : null
  )

  const poster_reference_href = computed(() =>
    poster_dom_href(/** @type {Id} */ (props.itemid))
  )
  const ref_dom_viewbox = ref('0 0 16 16')
  const ref_dom_preserve_aspect_ratio = ref('xMidYMid meet')
  const ref_dom_landscape = ref(false)

  const sync_reference_from_canonical = () => {
    const el = document.getElementById(
      poster_dom_id(/** @type {Id} */ (props.itemid))
    )
    if (!el || el.tagName !== 'svg') return
    const vb = el.getAttribute('viewBox')
    if (vb) ref_dom_viewbox.value = vb
    const par = el.getAttribute('preserveAspectRatio')
    if (par) ref_dom_preserve_aspect_ratio.value = par
    ref_dom_landscape.value =
      el.getAttribute('data-orientation') === 'horizontal'
  }

  const dom_reference_activate = () => {
    document.dispatchEvent(
      new CustomEvent(POSTER_MEET_TOGGLE_ONLY, {
        bubbles: true,
        detail: { itemid: props.itemid }
      })
    )
  }

  const {
    handle_pointerdown: on_dom_ref_pointerdown,
    handle_pointermove: on_dom_ref_pointermove,
    handle_pointerup: on_dom_ref_pointerup,
    handle_pointerleave: on_dom_ref_pointerleave,
    handle_contextmenu: on_dom_ref_contextmenu
  } = use_poster_svg_activate_pointer({
    on_activate: dom_reference_activate,
    touch_uses_long_press: true,
    is_disabled: computed(() => !use_dom_reference.value)
  })

  const haptic_id = computed(() => `haptic-${query_id.value}`)
  const haptic_supported = is_ios()
  const haptic_enabled = computed(
    () => haptic_supported && !canvas_alive.value && !mask_pen.active.value
  )
  const on_haptic_focus = () => poster.value?.focus()
  const on_haptic_pointer = event => {
    if (use_dom_reference.value) return
    as_svg_ref.value?.activate_pointer?.[event.type]?.(event)
  }
  const on_haptic_click = () => {
    if (use_dom_reference.value) on_poster_svg_click()
  }

  watch(haptic_enabled, enabled => {
    if (enabled) return
    as_svg_ref.value?.activate_pointer?.pointercancel()
    on_dom_ref_pointerleave()
  })

  const poster_label = computed(() => {
    const created = as_created_at(/** @type {Id} */ (props.itemid))
    if (!created) return 'Poster'
    const day = as_day(new Date(created))
    return `Poster from ${day === 'Today' ? 'today' : day}`
  })
  const poster_time = computed(() => {
    const created_at = as_created_at(/** @type {Id} */ (props.itemid))
    if (!created_at) return ''
    return as_time(new Date(created_at))
  })
  const profile_path = computed(
    () => as_author(/** @type {Id} */ (props.itemid)) || ''
  )
  const is_my_poster = computed(() => {
    if (typeof window === 'undefined') return false
    const my_id = window.localStorage?.me
    if (!my_id) return false
    return profile_path.value === my_id
  })
  const has_remove_handler = computed(() => !!instance?.vnode.props?.onRemove)
  const author_menu_poster = computed(() => ({
    id: props.itemid
  }))
  const profile_chip_itemid = computed(() =>
    props.profile_display === 'label'
      ? /** @type {Id | undefined} */ (props.itemid)
      : undefined
  )
  const shown = ref(false)
  const working = ref(true)
  /** Frame progress of a bare-poster audio video export, or null when idle. */
  const video_export_progress = ref(null)
  const unload_cutouts = () => {
    cutout_load_token.value += 1
    if (vector.value?.cutouts) delete vector.value.cutouts
    cutouts_loaded.value = false
  }

  /**
   * `missing` is answered by deleting the poster from storage - its html, its
   * shadow, every geology layer - so nothing may emit it on a hunch, and both
   * callers here are hunches. `show` in use/poster fires with whatever `vector`
   * holds, so a render racing the load reports empty for a poster that is
   * perfectly well there; and no cutout layers looks the same as a deletion
   * even though plenty of posters have none. Loading the poster is the only
   * evidence that counts: if it comes back, nothing is missing.
   *
   * @returns {Promise<Poster|null>} the poster, when it is still there
   */
  const confirm_missing = async caller => {
    const poster_loaded = await load(/** @type {Id} */ (props.itemid))
    poster_delete_log('as-figure confirm_missing', {
      itemid: props.itemid,
      caller,
      still_loads: Boolean(poster_loaded)
    })
    if (poster_loaded) return /** @type {Poster} */ (poster_loaded)
    emit('missing', props.itemid)
    return null
  }

  const load_cutouts = async () => {
    if (!vector.value) return
    if (should_skip_cutout_load()) return
    const token = cutout_load_token.value + 1
    cutout_load_token.value = token
    const next_cutouts = await load_cutout_flags(
      /** @type {Id} */ (props.itemid)
    )
    if (!vector.value || cutout_load_token.value !== token) return
    vector.value.cutouts = next_cutouts
    cutouts_loaded.value = true

    const created = as_created_at(/** @type {Id} */ (props.itemid))
    if (
      created &&
      created > GEOLOGY_DATE &&
      Object.keys(next_cutouts).length === 0
    )
      await confirm_missing('no cutout layers')
  }

  const on_show = async shown_vector => {
    if (!shown_vector) {
      const still_there = await confirm_missing('empty show')
      if (still_there) return on_show(still_there)
      return
    }

    working.value = true
    cutouts_loaded.value = false
    vector.value = shown_vector

    if (!shown_vector.regular)
      try {
        vector.value = await load_shadow_into_vector(
          /** @type {Poster} */ (vector.value),
          /** @type {Id} */ (props.itemid)
        )
      } catch {}

    if (vector.value && has_drawable_layer(vector.value)) {
      await tick()
      emit('show', vector.value)
      shown.value = true
    }
    working.value = false
  }

  provide('vector', vector)
  watch_effect(async () => {
    if (!use_dom_reference.value) return
    if (vector.value?.id === props.itemid) return
    const itemid_at_start = props.itemid
    const poster_loaded = await load(/** @type {Id} */ (itemid_at_start))
    if (props.itemid !== itemid_at_start) return
    if (poster_loaded) await on_show(/** @type {Poster} */ (poster_loaded))
  })
  watch(
    () => props.itemid,
    () => {
      if (use_dom_reference.value) sync_reference_from_canonical()
    }
  )
  watch_effect(async () => {
    const should_load_person = props.menu || menu_open.value
    if (should_load_person && !person.value) {
      const author_id = as_author(/** @type {Id} */ (props.itemid))
      // eslint-disable-next-line require-atomic-updates
      if (author_id) person.value = await load(/** @type {Id} */ (author_id))
    }
  })
  watch(
    () => props.menu,
    menu_enabled => {
      if (!menu_enabled) menu_open.value = false
    }
  )
  const cutouts_active = computed(
    () =>
      poster_intersecting.value ||
      props.pin ||
      view_3d.value ||
      am_canonical.value
  )

  watch_effect(async () => {
    if (use_dom_reference.value) return
    const mosaic_on = mosaic.value
    const vector_id = vector.value?.id
    if (!vector_id) return
    if ((mosaic_on || view_3d.value) && cutouts_active.value) {
      await load_cutouts()
      return
    }
    if (props.pin) return
    unload_cutouts()
  })

  updated(() => {
    scroll_to_hash_if_matching()
  })

  const key_commands = inject('key-commands', null)
  const set_working = inject('set_working', () => {})
  const as_svg_ref = ref(null)

  const set_svg_zoom = t => {
    const svg = as_svg_ref.value?.$el
    if (!svg) return
    if (t <= 0) svg.style.scale = ''
    else svg.style.scale = String(1 + (SVG_ZOOM_SCALE - 1) * t)
  }

  const start_canvas_leave = () => {
    if (!canvas_alive.value || canvas_leaving.value) return
    canvas_leaving.value = true
    viewer_ref.value?.start_leave(set_svg_zoom, () => {
      canvas_alive.value = false
      canvas_leaving.value = false
    })
  }

  const want_3d = computed(
    () =>
      view_3d.value &&
      shown.value &&
      cutouts_loaded.value &&
      poster_in_view.value
  )

  watch(want_3d, want => {
    if (want && !canvas_alive.value && !canvas_leaving.value)
      canvas_alive.value = true
    else if (!want && canvas_alive.value && !canvas_leaving.value)
      start_canvas_leave()
  })

  before_unmount(() => {
    if (typeof window !== 'undefined')
      window.removeEventListener('pointerdown', on_window_pointerdown)
  })

  const poster_toggle_target = () =>
    use_dom_reference.value
      ? { toggle_meet: dom_reference_activate }
      : as_svg_ref.value

  const poster_focused = ref(false)

  /**
   * The arrows mean "move the camera" in both views; which camera they reach
   * depends on which one is drawing. The 3d canvas listens for them itself, so
   * the svg camera only claims the Poster context while 3d is off - otherwise
   * one press moves both cameras at once.
   */
  watch_effect(() => {
    if (!poster_focused.value) return
    if (view_3d.value) key_commands?.remove_context('Poster')
    else key_commands?.add_context('Poster')
  })

  const on_focusin = () => {
    poster_focused.value = true
    key_commands?.register_handler('poster::Toggle_Meet_Slice', {
      handler: () => poster_toggle_target()?.toggle_meet?.(),
      context: 'Poster'
    })
  }

  const on_focusout = () => {
    poster_focused.value = false
    key_commands?.remove_context('Poster')
    key_commands?.unregister_handler('poster::Toggle_Meet_Slice')
  }

  /** @type {import('vue').Ref<Array<{ focus_editor?: () => void }>>} */
  const overlay_thought_refs = ref([])

  /**
   * An emptied statement is one line tall inside a panel several lines tall, so
   * most attempts to click back into it land on the panel and do nothing. The
   * panel is what you aim at, so the panel opens the last statement in it -
   * where a train of thought is still being written.
   * @param {MouseEvent} event
   */
  const on_overlay_click = event => {
    if (!props.overlay_editable) return
    if (event.target !== event.currentTarget) return
    const thoughts = overlay_thought_refs.value
    thoughts[thoughts.length - 1]?.focus_editor?.()
  }

  /**
   * Enter toggles meet/slice, and toggling it emits the click that opens and
   * closes the thought overlay - so Enter inside the overlay's own editor would
   * shut the overlay under the caret. Text entry keeps its Enter, poster and
   * caret both intact.
   * @param {KeyboardEvent} event
   */
  const on_activate_poster = event => {
    const target = /** @type {Element | null} */ (event?.target)
    if (
      target?.closest?.(
        'figcaption, [contenteditable="true"], input, textarea, select, button, a'
      )
    )
      return
    event?.preventDefault()
    poster_toggle_target()?.toggle_meet?.()
  }

  /**
   * Allow an audio drag over a poster so the drop registers (dragover must be
   * prevented for a drop event to fire). Everything else falls through to the
   * app-wide image-drop handling.
   * @param {DragEvent} event
   */
  const on_poster_dragover = event => {
    const files = Array.from(event.dataTransfer?.files || [])
    if (files.some(is_audio_file)) event.preventDefault()
  }

  /**
   * When an audio file is dropped on the poster, decode it and export the
   * video with it baked in as the soundtrack, looping the animation to match
   * the track length. Works on the bare poster - no menu needed - because it
   * calls the shared export directly rather than routing through the (lazily
   * mounted) download menu.
   * @param {DragEvent} event
   */
  const on_poster_drop_audio = async event => {
    const files = Array.from(event.dataTransfer?.files || [])
    const audio_files = files.filter(is_audio_file)
    if (!audio_files.length) return
    event.preventDefault()
    event.stopPropagation()

    let decoded = []
    try {
      decoded = await decode_audio_files(audio_files)
    } catch (error) {
      console.error('Failed to decode audio for video export:', error)
      return
    }
    if (!decoded.length) return

    video_export_progress.value = { frame: 0, total: 0 }
    set_working(true)
    try {
      await export_poster_to_video_with_audio(props.itemid, {
        audio_buffers: decoded,
        on_progress: (frame, total) => {
          video_export_progress.value = { frame, total }
        }
      })
    } finally {
      video_export_progress.value = null
      set_working(false)
    }
  }

  watch_effect(() => {
    if (!use_dom_reference.value) return
    sync_reference_from_canonical()
  })
</script>

<template>
  <figure
    ref="poster"
    :data-layer-view="layer_comparison && canvas_alive ? layer_view : undefined"
    :data-focus-pull="focus_pull_active || undefined"
    :aria-expanded="
      overlay_statements?.length ? overlay_text_visible : undefined
    "
    @focusin="on_focusin"
    @focusout="on_focusout"
    @keydown.enter="on_activate_poster"
    @dragover="on_poster_dragover"
    @drop="on_poster_drop_audio"
    @pointerdown="on_dom_ref_pointerdown"
    @pointermove="on_dom_ref_pointermove"
    @pointerup="on_dom_ref_pointerup"
    @pointerleave="on_dom_ref_pointerleave"
    @pointercancel="on_dom_ref_pointerleave"
    @contextmenu="on_dom_ref_contextmenu">
    <svg
      v-if="use_dom_reference"
      itemscope
      itemtype="https://realness.online/posters"
      :itemid="itemid"
      :viewBox="ref_dom_viewbox"
      :preserveAspectRatio="ref_dom_preserve_aspect_ratio"
      role="img"
      aria-roledescription="referenced poster"
      :aria-label="poster_label"
      :data-orientation="ref_dom_landscape ? 'horizontal' : 'vertical'"
      @click="on_poster_svg_click"
      @selectstart.prevent>
      <use :href="poster_reference_href" />
      <rect
        role="presentation"
        aria-hidden="true"
        x="0"
        y="0"
        width="100%"
        height="100%"
        fill="transparent" />
    </svg>
    <as-svg
      v-if="!use_dom_reference"
      ref="as_svg_ref"
      :itemid="itemid"
      :slice="slice"
      :sync_poster="sync_poster_for_svg"
      :show_cutout_layers="cutouts_active && mosaic"
      :pin="props.pin"
      :paused="!poster_in_view || (canvas_alive && !layer_comparison)"
      :behind_canvas="canvas_alive"
      :focusable="false"
      @show="on_show"
      @click="on_poster_svg_click" />
    <as-poster-symbol
      v-if="shown && !use_dom_reference"
      :itemid="itemid"
      :vector="vector"
      :show_cutout_symbols="cutouts_active && mosaic"
      :shown="shown" />
    <label
      v-if="haptic_enabled"
      :for="haptic_id"
      aria-hidden="true"
      @pointerdown="on_haptic_pointer"
      @pointermove="on_haptic_pointer"
      @pointerup="on_haptic_pointer"
      @pointerleave="on_haptic_pointer"
      @pointercancel="on_haptic_pointer"
      @contextmenu="on_haptic_pointer"
      @click="on_haptic_click"
      @selectstart.prevent>
      <input
        :id="haptic_id"
        type="checkbox"
        switch
        tabindex="-1"
        data-haptic
        @click.stop
        @focus="on_haptic_focus" />
    </label>
    <as-viewer-3d
      v-if="canvas_alive"
      ref="viewer_ref"
      :itemid="itemid"
      :on_svg_zoom="set_svg_zoom"
      data-mode="inline"
      @select="on_poster_svg_click" />
    <fieldset
      v-if="layer_comparison && canvas_alive"
      aria-label="Poster layers"
      @pointerdown.stop
      @pointerup.stop
      @click.stop
      @keydown.stop>
      <select v-model="layer_view" aria-label="Poster layers">
        <option value="svg">SVG only</option>
        <option value="3d">3D only</option>
        <option value="both">SVG + 3D</option>
      </select>
      <label>
        <input
          v-model="focus_pull"
          type="checkbox"
          aria-label="Focus pull"
          :disabled="
            layer_view !== 'both' || !animate_pref || reduced_motion
          " />
        Focus pull
      </label>
    </fieldset>
    <figcaption
      v-if="figcaption_visible"
      @pointerdown.stop
      @pointermove.stop
      @pointerup.stop
      @pointercancel.stop
      @contextmenu.stop>
      <header>
        <aside
          v-if="overlay_text_visible"
          aria-live="polite"
          @click="on_overlay_click">
          <as-thought
            v-for="stmt in overlay_statements"
            :key="stmt.id"
            ref="overlay_thought_refs"
            :thought="stmt"
            :editable="overlay_editable" />
        </aside>
      </header>
      <template
        v-if="
          menu_open || (menu && menu_always_visible) || mask_pen.active.value
        ">
        <menu
          v-if="mask_pen.active.value"
          class="mask-panel"
          aria-label="Poster subjects">
          <li
            v-for="(subject, index) in mask_pen.subjects.value"
            :key="subject.id">
            <span
              class="mask-swatch"
              :style="{ background: `hsl(${subject_hue(index)} 70% 55%)` }"
              aria-hidden="true" />
            <button
              :aria-pressed="subject.id === mask_pen.active_subject_id.value"
              @click.stop="mask_pen.select_subject(subject.id)">
              {{ subject.name }}
            </button>
            <input
              :value="subject.name"
              aria-label="Rename subject"
              @input="
                mask_pen.rename_subject(subject.id, $event.target.value)
              " />
            <button
              :aria-label="
                mask_pen.pending_removal_id.value === subject.id
                  ? `Confirm remove ${subject.name || 'subject'}`
                  : `Remove ${subject.name || 'subject'}`
              "
              :data-arm="
                mask_pen.pending_removal_id.value === subject.id || undefined
              "
              @click.stop="mask_pen.request_remove_subject(subject.id)">
              {{
                mask_pen.pending_removal_id.value === subject.id ? 'Sure?' : '×'
              }}
            </button>
          </li>
          <li>
            <button
              aria-label="Add subject"
              @click.stop="mask_pen.add_subject()">
              +
            </button>
          </li>
        </menu>
        <footer>
          <router-link
            v-if="
              poster_time &&
              is_my_poster &&
              profile_path &&
              !mask_pen.active.value
            "
            :to="profile_path">
            <time>{{ poster_time }}</time>
          </router-link>
          <time v-else-if="poster_time && !mask_pen.active.value">{{
            poster_time
          }}</time>
          <slot>
            <menu
              v-if="is_my_poster"
              :style="
                mask_pen.active.value && mask_pen.painting.value
                  ? { opacity: 0.12, pointerEvents: 'none' }
                  : null
              ">
              <button
                aria-label="Toggle mask pen"
                :aria-pressed="mask_pen.active.value"
                @click.stop="on_toggle_mask_pen">
                &#9998;<span v-if="mask_pen.selected.value.size">
                  {{ mask_pen.selected.value.size }}</span
                >
              </button>
              <button
                v-if="mask_pen.active.value && mask_pen.selected.value.size"
                aria-label="Clear mask"
                @click.stop="mask_pen.clear()">
                &times;
              </button>
              <as-author-menu
                v-if="!mask_pen.active.value"
                :poster="author_menu_poster"
                :allow_remove="has_remove_handler"
                @remove="id => emit('remove', id)" />
            </menu>
            <menu v-else>
              <as-figure
                v-if="person"
                :person="person"
                display="label"
                :itemid="profile_chip_itemid" />
              <span role="group">
                <as-download :itemid="/** @type {Id} */ (itemid)" />
              </span>
            </menu>
          </slot>
        </footer>
      </template>
    </figcaption>
    <output
      v-if="video_export_progress"
      class="video-export-progress"
      aria-live="polite">
      {{ video_export_progress.frame }}/{{ video_export_progress.total }} frames
    </output>
  </figure>
</template>

<style lang="stylus">
  @keyframes poster-focus-pull {
    0%, 10%, 33.3333%, 43.3333%, 66.6667%, 76.6667%, 100% {
      opacity: 0.5;
    }
    16.6667%, 26.6667%, 50%, 60%, 83.3333%, 93.3333% {
      opacity: 0.15;
    }
  }

  figure:has([itemtype$='/posters']):not([itemtype]) {
    position: relative;
    display: grid;
    overflow: hidden;
    min-height: 512px;
    border-radius: round((base-line * .03), 2);
    grid-row-start: span 2;
    transition:
      grid-column-start 0.4s cubic-bezier(0.22, 1, 0.36, 1),
      grid-column-end 0.4s cubic-bezier(0.22, 1, 0.36, 1),
      grid-row-start 0.4s cubic-bezier(0.22, 1, 0.36, 1),
      grid-row-end 0.4s cubic-bezier(0.22, 1, 0.36, 1),
      min-height 0.4s cubic-bezier(0.22, 1, 0.36, 1),
      width 0.4s cubic-bezier(0.22, 1, 0.36, 1);
    scroll-margin: 50vh;
    scroll-snap-align: center;
    &[aria-expanded='false']::after {
      content: '';
      position: absolute;
      top: round((base-line * 0.18), 2);
      right: round((base-line * 0.18), 2);
      bottom: round((base-line * 0.18), 2);
      left: round((base-line * 0.18), 2);
      z-index: 2;
      pointer-events: none;
      border: round((base-line * 0.1), 2) solid unquote('color-mix(in srgb, var(--accent) 88%, transparent)');
      border-radius: round((base-line * 0.28), 2);
    }
    &:has(svg[data-aspect]) {
      grid-column-start: span 3;
      grid-row-start: auto;
    }
    output.video-export-progress {
      position: absolute;
      left: 50%;
      bottom: base-line * 0.5;
      transform: translateX(-50%);
      z-index: 3;
      padding: base-line * 0.25 base-line * 0.5;
      font-size: 0.78em;
      white-space: nowrap;
      frosted-glass();
      border-radius: base-line * 0.25;
    }
    &:focus {
      outline: 0.25px solid var(--emphasis);
      outline-offset: base-line * 0.25;
    }
    @media (orientation: landscape), (min-width: page-width) {
      &:has(svg[data-orientation='horizontal']) {
        grid-column-start: span 2;
        grid-row-start: auto;
        width: 100%;
        min-height: auto;
        &.new {
          grid-column-start: span 2;
        }
        &:has(svg[data-aspect]) {
          grid-column-start: span 2;
        }
      }
    }
    @media (min-width: pad-begins) {
      &:has(svg[data-orientation='horizontal']):has(+ figure:has([itemtype$='/posters']):has(svg[data-orientation='horizontal'])) {
        grid-column-start: span 3;
      }
      &:has(svg[data-orientation='horizontal']) + figure:has([itemtype$='/posters']):has(svg[data-orientation='horizontal']) {
        grid-column-start: span 3;
      }
      &.new:not(:has(svg[data-orientation='horizontal'])) {
        grid-column: 2;
        grid-row: 2;
      }
    }
    svg {
      z-index: 1;
      &[itemscope] {
        grid-area: 1 / 1;
        position: relative;
      }
    }
    & > svg:not([data-poster-symbol-defs]) {
      content-visibility: auto;
      contain-intrinsic-size: auto 512px;
    }
    & > svg[itemid] {
      display: block;
      min-height: 512px;
      height: 100%;
      width: 100%;
      overflow: hidden;
      cursor: pointer;
      disable-ios-touch-callout();
      touch-action: pan-y pinch-zoom;
      contain: layout;
      max-height: 100%;
      &[data-storytelling] {
        touch-action: pan-x pinch-zoom;
      }
    }
    & > svg[itemid] rect[role='presentation'][aria-hidden='true'] {
      pointer-events: all;
    }
    & > label:has(> input[data-haptic]) {
      grid-area: 1 / 1;
      position: relative;
      z-index: 2;
      cursor: pointer;
      disable-ios-touch-callout();
      touch-action: pan-y pinch-zoom;
    }
    &:has(> svg[data-storytelling]) > label:has(> input[data-haptic]) {
      touch-action: pan-x pinch-zoom;
    }
    input[data-haptic] {
      all: initial;
      appearance: auto;
      position: absolute;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
    }
    @media (prefers-reduced-motion: reduce) {
      transition-duration: 0.01ms;
    }
    & > fieldset:has(> select[aria-label='Poster layers']) {
      position: absolute;
      top: base-line;
      left: base-line;
      z-index: 5;
      display: flex;
      align-items: center;
      gap: base-line * 0.5;
      padding: base-line * 0.5;
      font-size: 0.78em;
      color: var(--text);
      border: 0;
      margin: 0;
      frosted-glass();
      border-radius: base-line * 0.25;
      select {
        appearance: auto;
        background: transparent;
        cursor: pointer;
      }
      label {
        display: flex;
        align-items: center;
        gap: base-line * 0.25;
        white-space: nowrap;
      }
      input[type='checkbox'] {
        appearance: auto;
        width: 1em;
        height: 1em;
        margin: 0;
        accent-color: var(--accent);
        background: transparent;
      }
    }
    &[data-layer-view='3d'] > svg[itemid] {
      visibility: hidden;
    }
    &[data-layer-view='svg'] > [data-mode='inline'] {
      visibility: hidden;
    }
    &[data-layer-view='both'] > [data-mode='inline'] {
      opacity: 0.5;
    }
    &[data-focus-pull] > [data-mode='inline'] {
      animation: poster-focus-pull 30s ease-in-out infinite;
      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }
    [data-mode='inline'] {
      position: absolute;
      inset: 0;
      z-index: 2;
      disable-ios-touch-callout();
      border-radius: round((base-line * .03), 2);
      overflow: hidden;
    }
    & > figcaption {
      grid-area: 1 / 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      transform: none;
      z-index: 3;
      pointer-events: none;
      padding: base-line;
      & > header {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: base-line * 0.5;
        flex: 1;
        min-height: 0;
        pointer-events: none;
        & > aside {
          display: block ;
          min-height: 0;
          max-height: none;
          overflow-y: auto;
          overflow-x: hidden;
          z-index: 2;
          padding: base-line;
          border-radius: base-line * 0.5;
          frosted-glass();
          color: var(--text);
          box-shadow:
            0 0.08em 0.6em unquote('color-mix(in srgb, var(--basalt) 5%, transparent)'),
            0 0.15em 0.5em unquote('color-mix(in srgb, var(--moonlight) 15%, transparent)');
          @media (prefers-color-scheme: dark) {
            box-shadow:
              0 0.08em 0.6em unquote('color-mix(in srgb, var(--moonlight) 35%, transparent)'),
              0 0.2em 0.65em unquote('color-mix(in srgb, var(--moonlight) 45%, transparent)');
          }
          -webkit-overflow-scrolling: touch;
          pointer-events: auto;
          & p[itemprop='statement'] {
            font-size: 0.95em;
            line-height: 1.45;
            white-space: pre-wrap;
            word-break: break-word;
            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }
      & > menu.mask-panel {
        position: absolute;
        top: base-line;
        left: base-line;
        z-index: 4;
        max-height: 60%;
        overflow-y: auto;
        pointer-events: auto;
        margin: 0;
        padding: base-line * 0.5;
        border-radius: base-line * 0.5;
        frosted-glass();
        box-shadow:
          0 0.08em 0.6em unquote('color-mix(in srgb, var(--basalt) 5%, transparent)'),
          0 0.15em 0.5em unquote('color-mix(in srgb, var(--moonlight) 15%, transparent)');
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: base-line * 0.25;
        & > li {
          display: flex;
          align-items: center;
          gap: base-line * 0.25;
          padding: base-line * 0.2;
          border: round(base-line * 0.1, 2) solid unquote('color-mix(in srgb, var(--bone) 30%, transparent)');
          border-radius: base-line * 0.25;
          background: unquote('color-mix(in srgb, var(--moonlight) 30%, transparent)');
          & > .mask-swatch {
            width: base-line * 0.75;
            height: base-line * 0.75;
            border-radius: 50%;
            flex: none;
            box-shadow: inset 0 0 0 round(base-line * 0.1, 2) unquote('color-mix(in srgb, var(--basalt) 30%, transparent)');
          }
          & > button {
            border: none;
            background: transparent;
            color: var(--contrast);
            cursor: pointer;
            opacity: 0.7;
            &:hover { opacity: 1; }
            &[aria-pressed='true'] {
              opacity: 1;
              color: var(--accent);
              font-weight: 600;
            }
            &[data-arm] {
              opacity: 1;
              color: #c0504d;
              font-weight: 600;
            }
          }
          & > input {
            width: 7em;
            background: transparent;
            border: none;
            border-bottom: round(base-line * 0.1, 2) solid unquote('color-mix(in srgb, var(--bone) 40%, transparent)');
            color: var(--contrast);
            font: inherit;
            &:focus { outline: none; }
          }
        }
      }
      & > footer {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: base-line * 0.5;
        & > a:has(> time) {
          pointer-events: auto;
          text-decoration: none;
          &:hover > time,
          &:focus-visible > time {
            text-decoration: underline;
          }
        }
        & > a > time,
        & > time {
          color: var(--accent);
          white-space: nowrap;
          padding-inline: base-line * 0.4;
          border-radius: base-line * 0.25;
          frosted-glass();
        }
      }

      & > footer > menu {
        position: relative;
        &::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: -1;
          border-radius: inherit;
          frosted-glass();
        }
        pointer-events: auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: base-line * 0.5;
        padding: base-line (base-line * 0.5);
        height: auto;
        border-radius: base-line;
        min-width: 0;
        max-width: page-width;
        svg[itemtype$='/posters'] {
          min-height: auto;
        }
        & > a,
        & > figure,
        & > nav,
        & > button,
        & > fieldset,
        & > span[role='group'] {
          pointer-events: auto;
        }
        & > button[aria-label='Toggle mask pen'],
        & > button[aria-label='Clear mask'] {
          background: unquote('color-mix(in srgb, var(--moonlight) 30%, transparent)');
          border: round(base-line * 0.1, 2) solid unquote('color-mix(in srgb, var(--bone) 30%, transparent)');
          border-radius: base-line * 0.25;
          padding-inline: base-line * 0.5;
          cursor: pointer;
          color: var(--contrast);
          font-size: larger;
          line-height: 1;
          opacity: 0.7;
          min-width: base-line * 1.5;
          text-align: center;
          &:hover { opacity: 1; background: unquote('color-mix(in srgb, var(--moonlight) 50%, transparent)'); }
        }
        & > button[aria-label='Toggle mask pen'][aria-pressed='true'] {
          opacity: 1;
          background: unquote('color-mix(in srgb, var(--slate-fill) 50%, transparent)');
          border-color: unquote('color-mix(in srgb, var(--slate-fill) 80%, transparent)');
        }
        & > span[role='group'] {
          display: flex;
          gap: base-line;
          align-items: center;
        }
        & > a[data-display='label'],
        & > figure[data-display='phonebook'] {
          z-index: 2;
          position: relative;
          animation-name: fade-in;
          animation-duration: 0.1s;
          padding: base-line * .33;
          frosted-glass();
          border-radius: base-line * .25;
          & > address {
            & > h3:first-of-type {
              margin-right: base-line * .333;
            }
            & > h3,
            & > time {
              color: var(--accent);
              line-height: 1;
            }
          }
        }
        & > a[data-display='label'] > svg,
        & > figure[data-display='phonebook'] > svg,
        & > span[role='group'] svg {
         fill: var(--accent);
        }
      }

      & > footer > menu > menu {
        display: flex;
        align-items: center;
        gap: base-line * 0.5;
      }
    }
  }
</style>
